import { useEffect, useRef, useState } from 'react';
import {
  CAL_FROM,
  CAL_ROW,
  CAL_TO,
  columnHeightPx,
  formatHours,
  formatMinutes,
  minutesToPx,
  placeDay,
  snapMinutes,
  type Worklog,
} from '@forecastify/shared';
import { Tooltip } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The calendar view — `markup.html` 828–871, `main.jsx` 1223–1291.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Seven day columns, 07:00–19:00 at 48px an hour, entries placed by `placeDay` and drag-to-create
 * anywhere in a column. The arithmetic is all in `@forecastify/shared` with its own tests; what is
 * here is the pointer handling and the DOM.
 *
 * ⚠️⚠️ **A DRAG IS NOT THE ONLY WAY TO CREATE AN ENTRY HERE, AND IT CANNOT BE.** No key event
 * starts a pointer drag, so a drag-only calendar is unusable without a mouse — the board records the
 * same finding for its cards. Every column carries a keyboard-reachable button that opens the same
 * dialog with the same default range, so the feature has a path that does not need a pointer.
 *
 * ⚠️ **Pointer listeners go on the WINDOW once a drag starts, not on the column.** A drag that
 * leaves the element it began in stops receiving `mousemove` and the selection freezes mid-gesture
 * — the Gantt's header records the identical fix.
 */

interface Drag {
  readonly day: string;
  readonly from: number;
  readonly to: number;
}

export interface CalendarRange {
  readonly day: string;
  /** `HH:MM`. */
  readonly from: string;
  readonly to: string;
}

export function LogsCalendar({
  days,
  entries,
  today,
  canLog,
  onCreate,
  onOpen,
}: {
  /** Seven wire dates, Monday first. */
  readonly days: readonly string[];
  readonly entries: readonly Worklog[];
  readonly today: string;
  readonly canLog: boolean;
  readonly onCreate: (range: CalendarRange) => void;
  readonly onOpen: (entry: Worklog) => void;
}) {
  const t = useT();
  const [drag, setDrag] = useState<Drag | null>(null);
  const columns = useRef(new Map<string, HTMLDivElement>());

  /**
   * ⚠️ **The listeners are attached for the duration of the drag and removed after.** Left on
   * permanently they would fire on every pointer move in the app for as long as the calendar is
   * mounted, which is a cost nobody can see and everybody pays.
   */
  useEffect(() => {
    if (drag === null) return;

    const minutesAt = (clientY: number): number => {
      const column = columns.current.get(drag.day);
      if (column === undefined) return drag.to;
      const rect = column.getBoundingClientRect();
      const y = Math.min(Math.max(clientY - rect.top, 0), rect.height);
      return snapMinutes(CAL_FROM + (y / rect.height) * (CAL_TO - CAL_FROM));
    };

    const move = (event: MouseEvent): void => {
      setDrag((current) =>
        current === null ? null : { ...current, to: minutesAt(event.clientY) },
      );
    };

    const up = (event: MouseEvent): void => {
      const to = minutesAt(event.clientY);
      const low = Math.min(drag.from, to);
      /* ⚠️ **A click is a one-hour entry, not a zero-minute one.** Pressing without moving is how
       * most people "pick a time", and a zero-length range would open a dialog refusing itself. */
      const high =
        Math.max(drag.from, to) - low < 15 ? Math.min(CAL_TO, low + 60) : Math.max(drag.from, to);
      setDrag(null);
      onCreate({ day: drag.day, from: formatMinutes(low), to: formatMinutes(high) });
    };

    /* 17 Oct 2026 · the same drag by finger, once a held touch has started it (`holdStart`).
       ⚠️ Non-passive, so the column stops the page scrolling while the range is drawn. */
    const touchMove = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (touch === undefined) return;
      event.preventDefault();
      move({ clientY: touch.clientY } as MouseEvent);
    };
    const touchEnd = (event: TouchEvent): void => {
      /* ⚠️ Or the browser follows with a synthetic click, which would start a second range. */
      event.preventDefault();
      const touch = event.changedTouches[0];
      up({ clientY: touch?.clientY ?? 0 } as MouseEvent);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd, { passive: false });
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
    };
  }, [drag, onCreate]);

  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ 17 Oct 2026 · A FINGER DRAWS A RANGE BY HOLDING FIRST.          │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * On a phone a drag down a column only scrolled the week. Now a finger that rests on a free slot
   * for `HOLD_MS` without moving starts the range there, and dragging draws it; a finger that moves
   * first is a scroll, as before. A tap still opens the one-hour default, through the mouse events
   * the browser synthesises after it.
   */
  const holdStart = (day: string, event: React.TouchEvent<HTMLDivElement>): void => {
    if (!canLog) return;
    const touch = event.touches[0];
    if (touch === undefined) return;
    const column = event.currentTarget;
    const start = { x: touch.clientX, y: touch.clientY };
    const timer = window.setTimeout(() => {
      cleanup();
      const rect = column.getBoundingClientRect();
      const y = Math.min(Math.max(start.y - rect.top, 0), rect.height);
      const at = snapMinutes(CAL_FROM + (y / rect.height) * (CAL_TO - CAL_FROM));
      setDrag({ day, from: at, to: at });
    }, HOLD_MS);
    const early = (moved: TouchEvent): void => {
      const now = moved.touches[0];
      if (now === undefined) return;
      if (Math.abs(now.clientX - start.x) > SLOP || Math.abs(now.clientY - start.y) > SLOP) {
        cleanup();
      }
    };
    const cleanup = (): void => {
      window.clearTimeout(timer);
      window.removeEventListener('touchmove', early);
      window.removeEventListener('touchend', cleanup);
      window.removeEventListener('touchcancel', cleanup);
    };
    window.addEventListener('touchmove', early, { passive: true });
    window.addEventListener('touchend', cleanup);
    window.addEventListener('touchcancel', cleanup);
  };

  const hours: number[] = [];
  for (let hour = CAL_FROM / 60; hour < CAL_TO / 60; hour += 1) hours.push(hour);

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* ── the day headers ─────────────────────────────────────────────────────────────── */}
      <div className="flex shrink-0 border-b">
        <div className="w-[58px] shrink-0" />
        {days.map((day, index) => {
          const total = entries
            .filter((entry) => entry.workedOn === day)
            .reduce((sum, entry) => sum + Number(entry.hours), 0);

          return (
            <div
              key={day}
              className={[
                'min-w-0 flex-1 border-l px-2 py-1.5 text-center',
                day === today ? 'bg-primary/5' : index > 4 ? 'bg-muted/30' : '',
              ].join(' ')}
            >
              <div className="text-muted-foreground text-[11px] font-medium tracking-[.03em] uppercase">
                {dowLabel(t, index)}
              </div>
              <div className="mt-0.5 flex items-center justify-center gap-1.5">
                <span
                  className={[
                    'text-[13px] font-medium tabular-nums',
                    day === today ? 'text-primary' : '',
                  ].join(' ')}
                >
                  {`${String(Number(day.slice(8)))}.`}
                </span>
                {/* ⚠️ Nothing rather than `0:00` for a day with no entries — the strip's rule. */}
                <span className="text-muted-foreground text-[11px] tabular-nums">
                  {total === 0 ? '' : formatHours(total)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex min-h-0 flex-1 overflow-y-auto">
        {/* ── the hour gutter ───────────────────────────────────────────────────────────── */}
        <div className="w-[58px] shrink-0" aria-hidden>
          {hours.map((hour) => (
            <div key={hour} className="relative" style={{ height: CAL_ROW }}>
              <span className="text-muted-foreground absolute -top-[7px] right-2 text-[10.5px] tabular-nums">
                {`${String(hour).padStart(2, '0')}:00`}
              </span>
            </div>
          ))}
        </div>

        {days.map((day, index) => {
          const placed = placeDay(entries.filter((entry) => entry.workedOn === day));
          const active = drag?.day === day ? drag : null;
          const low = active === null ? 0 : Math.min(active.from, active.to);
          const high = active === null ? 0 : Math.max(active.from, active.to);

          return (
            <div
              key={day}
              ref={(node) => {
                if (node === null) columns.current.delete(day);
                else columns.current.set(day, node);
              }}
              onTouchStart={(event) => {
                holdStart(day, event);
              }}
              onMouseDown={(event) => {
                if (!canLog || event.button !== 0) return;
                event.preventDefault();
                const rect = event.currentTarget.getBoundingClientRect();
                const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
                const at = snapMinutes(CAL_FROM + (y / rect.height) * (CAL_TO - CAL_FROM));
                setDrag({ day, from: at, to: at });
              }}
              className={[
                'relative min-w-0 flex-1 border-l select-none',
                canLog ? 'cursor-crosshair' : '',
                index > 4 ? 'bg-muted/20' : '',
              ].join(' ')}
              style={{ height: columnHeightPx() }}
            >
              {hours.map((hour) => (
                <div key={hour} className="border-b" style={{ height: CAL_ROW }} />
              ))}

              {placed.map((item) => {
                const from = Math.max(CAL_FROM, item.from);
                const to = Math.min(CAL_TO, item.to);
                const width = 96 / item.lanes;
                const height = Math.max(20, minutesToPx(to) - minutesToPx(from) - 2);
                const colour = item.entry.projectColor ?? 'var(--muted-foreground)';
                const range = `${formatMinutes(item.from)}–${formatMinutes(item.to)}`;

                return (
                  <Tooltip
                    key={item.entry.id}
                    asChild
                    content={`${item.entry.taskKey} · ${item.entry.taskTitle} · ${formatHours(item.entry.hours)}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onOpen(item.entry);
                      }}
                      /* ⚠️ `mousedown` must not reach the column, or clicking an entry starts a drag
                       * behind it and opens the create dialog on top of the edit one. */
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      className="absolute flex flex-col items-stretch justify-start overflow-hidden rounded-[5px] border px-1.5 py-0.5 text-left transition-shadow hover:shadow-sm"
                      style={{
                        top: minutesToPx(from),
                        height,
                        left: `${String(2 + item.lane * width)}%`,
                        width: `${String(width - 1)}%`,
                        backgroundColor: 'var(--card)',
                        backgroundImage: `linear-gradient(${colour}2b, ${colour}2b)`,
                        borderColor: `${colour}80`,
                        color: 'var(--foreground)',
                      }}
                    >
                      {/* ⚠️ Three densities by height, as the mockup does — a 20px block cannot hold
                        three lines, and clipping them is worse than choosing one. */}
                      {height < 34 ? (
                        <span className="block truncate text-[10.5px] font-medium tabular-nums">
                          {`${formatMinutes(item.from)} ${item.entry.taskKey}`}
                        </span>
                      ) : (
                        <>
                          <span className="block truncate text-[10px] tabular-nums opacity-70">
                            {range}
                          </span>
                          <span className="block truncate text-[11px] font-medium tabular-nums">
                            {item.entry.taskKey}
                          </span>
                          {height >= 54 && (
                            <span className="block truncate text-[11px] opacity-80">
                              {item.entry.note ?? item.entry.taskTitle}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  </Tooltip>
                );
              })}

              {active !== null && high > low && (
                <div
                  className="border-primary bg-primary/15 pointer-events-none absolute right-1 left-1 rounded-[5px] border border-dashed"
                  style={{
                    top: minutesToPx(low),
                    height: Math.max(12, minutesToPx(high) - minutesToPx(low)),
                  }}
                >
                  <span className="text-primary block px-1.5 py-0.5 text-[10.5px] font-medium tabular-nums">
                    {`${formatMinutes(low)}–${formatMinutes(high)}`}
                  </span>
                </div>
              )}

              {day === today && nowMinutes >= CAL_FROM && nowMinutes <= CAL_TO && (
                <div
                  aria-hidden
                  className="bg-destructive pointer-events-none absolute right-0 left-0 h-px"
                  style={{ top: minutesToPx(nowMinutes) }}
                />
              )}

              {/**
               * ⚠️⚠️ **THE KEYBOARD PATH. Without it this view is pointer-only.** It opens the same
               * dialog the drag opens, with a 09:00–10:00 default somebody can then edit — which is
               * what the dialog is for. Positioned last so it does not sit under the entries, and
               * only its focus ring makes it visible, so it costs the layout nothing.
               */}
              {canLog && (
                <button
                  type="button"
                  onClick={() => {
                    onCreate({ day, from: '09:00', to: '10:00' });
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  className="focus-visible:ring-ring absolute inset-x-1 bottom-1 h-5 rounded-[5px] text-[10px] opacity-0 focus-visible:opacity-100 focus-visible:ring-2"
                >
                  {t('logs.calendar.add', { day })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** ⚠️ One `t()` per branch with a literal key — a computed key is invisible to anything scanning. */
function dowLabel(t: Translate, index: number): string {
  switch (index) {
    case 0:
      return t('time.dow.mon');
    case 1:
      return t('time.dow.tue');
    case 2:
      return t('time.dow.wed');
    case 3:
      return t('time.dow.thu');
    case 4:
      return t('time.dow.fri');
    case 5:
      return t('time.dow.sat');
    default:
      return t('time.dow.sun');
  }
}

/** 17 Oct 2026 · how long a finger rests before it draws, and how far it may wander meanwhile. */
const HOLD_MS = 350;
const SLOP = 8;
